import type {
  ParameterTimeSeriesConfiguration,
  TimeSeriesWidgetConfiguration,
  WidgetInputsDefinition,
} from "@/lib/api/generated/types";

import { toParameterTimeSeriesConfigurationBody } from "./parameter-time-series-configuration.utils";

/** Dashboard selectors this widget type always consumes. */
export const TIME_SERIES_WIDGET_INPUTS: WidgetInputsDefinition = {
  location: true,
  time_range: true,
};

function readRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object") return null;
  return value as Record<string, unknown>;
}

export function isTimeSeriesWidgetConfiguration(
  config: unknown,
): config is TimeSeriesWidgetConfiguration {
  const record = readRecord(config);
  return record?.type === "time_series";
}

/**
 * Reads the singular chart config from a `time_series` widget.
 * OpenAPI: `TimeSeriesWidgetConfiguration.parameter_time_series_configuration`.
 */
export function readTimeSeriesConfiguration(
  config: unknown,
): ParameterTimeSeriesConfiguration | undefined {
  if (!isTimeSeriesWidgetConfiguration(config)) return undefined;
  const chart = config.parameter_time_series_configuration;
  if (!chart || typeof chart !== "object") return undefined;
  return chart;
}

/**
 * Maps the CMS form (loaded chart shape with `unit`) to the save body
 * (`unit_code`) required by create/update `TimeSeries` widget configuration.
 */
export function normalizeTimeSeriesWidgetFormValues<
  T extends { title?: string; configuration?: unknown },
>(values: T): T {
  const config = readRecord(values.configuration);
  if (!config || config.type !== "time_series") return values;

  const chart = readTimeSeriesConfiguration(config);
  if (!chart) return values;

  return {
    ...values,
    // Layout tabs read Widget.title; the CMS editor only writes the chart
    // title. Chart wins so renaming the widget updates the page layout.
    title: chart.title.trim() || values.title?.trim(),
    configuration: {
      type: "time_series",
      inputs: TIME_SERIES_WIDGET_INPUTS,
      parameter_time_series_configuration: toParameterTimeSeriesConfigurationBody(chart),
    },
  };
}
